import { ValueFormatterParams as AGValueFormatterParams } from "ag-grid-community";
import * as DateUtils from "src/utils/date-utils";
import { convertFractionToInning } from "src/utils/inningConverters";

export type ValueFormatterParams = Pick<AGValueFormatterParams, "value">;

export const fourDigitNumberFormatter = (params: ValueFormatterParams) => {
  const { value } = params;
  if (!value || value === 0) {
    return ".000";
  }
  const isNegative = value < 0;
  const abs = Math.abs(value);

  const absoluteString = abs.toString();
  let { length } = absoluteString;
  if (absoluteString.includes(".")) {
    length = absoluteString.split(".")[0].length;
  }
  const precision = Math.max(4 - length, 0);

  const displayNumber = isNegative ? -abs : abs;
  const formattedDisplayNumber = displayNumber.toFixed(precision);
  if (formattedDisplayNumber[0] === "0" || formattedDisplayNumber.includes("-0.")) {
    return formattedDisplayNumber.replace("0.", ".");
  }
  return formattedDisplayNumber;
};

export const plusMinusFormatter = (params: ValueFormatterParams) => {
  if (!params.value) {
    return "-";
  } else if (params.value > 0) {
    return `+${params.value}`;
  } else {
    return params.value;
  }
};

export const decimalFormatter = (precision: number) => {
  return (params: ValueFormatterParams) => {
    const { value } = params;
    return value ? value.toFixed(precision) : (0).toFixed(precision);
  };
};

export const hoopsDecimalFormatter = (precision: number) => {
  return (params: ValueFormatterParams) => {
    const { value } = params;
    return value ? value.toFixed(precision) : "-";
  };
};

/**
 * Pitch velocity (MPH) to one decimal, e.g. 76.4. Renders "N/A" when there's no reading (0 /
 * null / undefined) since GameChanger only carries a velocity for teams that ran a radar.
 */
export const velocityFormatter = (params: ValueFormatterParams): string => {
  const { value } = params;
  return value ? value.toFixed(1) : "N/A";
};

export const percentFormatter = (params: ValueFormatterParams) => {
  const value = params.value ? params.value * 100 : 0;
  return value === 100 ? value.toFixed(1) : value.toFixed(2);
};

export const roundedPercentFormatter = (params: ValueFormatterParams) => {
  if (params.value !== null && params.value !== undefined) {
    return `${Math.round(params.value * 100)}%`;
  } else {
    return "-";
  }
};

export const inningsFormatter = (params: ValueFormatterParams): string => {
  return convertFractionToInning(params.value || 0);
};

export const eventDateFormatter = (params: ValueFormatterParams): string => {
  if (params.value === "Totals") return "Totals";
  const scrimmage = params.value.includes(" *");
  let formattedDate = DateUtils.formatDate(params.value.replace(" *", ""), undefined, DateUtils.DATE_FORMAT.rrule);
  if (scrimmage) formattedDate += " *";
  return formattedDate;
};

export const rangeFormatter = (params: ValueFormatterParams): string => {
  const { value } = params;
  if (value && typeof value === "string") return value;
  return "0-0";
};

export const toStringFormatter = (param: ValueFormatterParams) => (param.value ?? "").toString();
